import * as THREE from 'three/webgpu';
import type { SketchGroup } from '../../mesh/sketch';
import type { PointSelectionCandidate } from '../../reference/reference-point/ReferencePointController';
import type { SketchRuntimeHistorySession, Viewer } from '../../viewer/Viewer';
import { ViewportCursorStyle } from '../../viewer/moudles';
import { AbstractSketchEntitiesTransformController } from './AbstractSketchEntitiesTransformController';
import { SketchPlanePointerIntersectionResolver } from './SketchPlanePointerIntersectionResolver';
import { SketchRotateCenterAxisHelper } from './SketchRotateCenterAxisHelper';
import { SketchRotatePointerDragSession } from './SketchRotatePointerDragSession';
import {
	SketchEntitiesTransformCommand,
	SketchEntitiesTransformPhase,
	SketchEntitiesTransformRelationRetentionMode,
	SketchEntitiesTransformSelectionRole,
	type SketchEntitiesTransformRepeatRecord,
} from './SketchEntitiesTransformTypes';
import {
	SketchRelationRetentionService,
	type SketchRelationRetentionContext,
} from './SketchRelationRetentionService';
import { clearSketchEntitiesTransformPointSnapFeedback } from './utils/SketchEntitiesTransformPointSnapResolver';

/**
 * 草图旋转实体控制器。
 */
export class SketchRotateEntitiesController extends AbstractSketchEntitiesTransformController {
	//------ 旋转实体确认式历史会话 开始------
	/** 几何关系保留服务。 */
	private readonly relationRetentionService = new SketchRelationRetentionService();
	/** 预览阶段临时断开的旋转阻塞关系上下文。 */
	private relationRetentionPreviewContext: SketchRelationRetentionContext | null = null;
	/** 旋转预览基线点位快照。 */
	private rotatePreviewBaseline: Map<string, THREE.Vector3> | null = null;
	/** 当前是否已经把旋转预览应用到草图实体。 */
	private rotatePreviewApplied = false;
	/** 旋转确认阶段使用的运行时历史会话。 */
	private rotateRuntimeHistorySession: SketchRuntimeHistorySession | null = null;
	//------ 旋转实体确认式历史会话 结束------

	//------ 旋转中心三轴辅助对象 开始------
	/** 旋转中心三轴辅助对象。 */
	private rotateCenterAxisHelper: SketchRotateCenterAxisHelper | null = null;
	/** 鼠标旋转角度拖动会话。 */
	private readonly pointerAngleDragSession = new SketchRotatePointerDragSession();
	/** 鼠标与草图平面的自由交点解析器。 */
	private readonly planePointerIntersectionResolver = new SketchPlanePointerIntersectionResolver();
	/** 当前是否已经压入拖动中光标。 */
	private hasPointerAngleGrabbingCursor = false;
	//------ 旋转中心三轴辅助对象 结束------

	/**
	 * @param viewer Viewer 实例。
	 */
	public constructor(viewer: Viewer, ) {
		super(viewer);
	}

	
	public override start(initialEntities: readonly THREE.Object3D[] = []): void {
		this.resetPointerAngleDrag();
		super.start(initialEntities);
		this.angleDeg = 0;
		this.refreshPhase();
		this.notifyStateChanged();
	}

	
	public override clear(): void {
		this.resetPointerAngleDrag();
		this.rollbackPreview();
		this.rotatePreviewBaseline = null;
		this.rotatePreviewApplied = false;
		this.cancelRotateRuntimeHistorySession();
		this.clearRotateCenterAxisHelper();
		clearSketchEntitiesTransformPointSnapFeedback(this.viewer);
		super.clear();
	}

	
	public override dispose(): void {
		this.resetPointerAngleDrag();
		this.rollbackPreview();
		this.rotatePreviewBaseline = null;
		this.rotatePreviewApplied = false;
		this.cancelRotateRuntimeHistorySession();
		this.clearRotateCenterAxisHelper();
		clearSketchEntitiesTransformPointSnapFeedback(this.viewer);
		super.dispose();
	}

	/**
	 * 设置旋转命令当前选择角色。
	 *
	 * @param role 目标选择角色。
	 *
	 * 设计说明：
	 * 1. 捕捉反馈只属于旋转中心取点阶段，切换输入框时不能遗留旧图标或高亮；
	 * 2. 再次激活旋转中心输入时必须先回滚旧预览并清空旧中心，才能建立新的单值选择；
	 * 3. 反馈由公共点捕捉管理器创建，因此仍通过实体变换捕捉出口统一回收；
	 * 4. 切换到实体选择时保留已经确认的旋转中心，避免编辑实体选择集时丢失参数。
	 */
	public override setActiveSelectionRole(role: SketchEntitiesTransformSelectionRole): void {
		this.resetPointerAngleDrag();
		if (role === SketchEntitiesTransformSelectionRole.RotationCenter) {
			// 旧角度预览必须先回滚，再归零参数；否则新中心确认后会立即重新套用旧角度。
			this.rollbackPreview();
			this.angleDeg = 0;
			this.rotationCenter = null;
			this.clearRotateCenterAxisHelper();
		}
		clearSketchEntitiesTransformPointSnapFeedback(this.viewer);
		super.setActiveSelectionRole(role);
	}

	/**
	 * 设置旋转角度并退出可能存在的控标拖动态。
	 *
	 * @param value PropertyManager 输入的角度值。
	 */
	public override setAngleDeg(value: number | null): void {
		this.resetPointerAngleDrag();
		super.setAngleDeg(value);
	}

	//------ 旋转中心三轴辅助对象 开始------
	
	public override handleReferencePointSelection(candidate: PointSelectionCandidate): boolean {
		if (this.activeSelectionRole !== SketchEntitiesTransformSelectionRole.RotationCenter) {
			// 场景旋转态的普通 click 不能误入父类参考点入口，否则父类会先回滚现有预览。
			return false;
		}
		const handled = super.handleReferencePointSelection(candidate);
		if (!handled) {
			return false;
		}
		// 中心点已经固定后立即清理瞬态捕捉反馈，只保留用于角度预览的旋转中心三轴标记。
		clearSketchEntitiesTransformPointSnapFeedback(this.viewer);
		// 中心确认后结束视口取点，后续输入焦点交给 PropertyManager 的角度字段。
		super.setActiveSelectionRole(SketchEntitiesTransformSelectionRole.None);
		return true;
	}

	/**
	 * 尝试从场景按下位置开始鼠标角度拖动。
	 *
	 * @param clientX 鼠标客户端 X 坐标。
	 * @param clientY 鼠标客户端 Y 坐标。
	 * @returns 当前命令允许旋转且按下位置形成有效径向向量时返回 true。
	 */
	public handlePointerAngleDragStart(clientX: number, clientY: number): boolean {
		if (
			!this.active
			|| !this.canConfirm()
			|| this.activeSelectionRole !== SketchEntitiesTransformSelectionRole.None
			|| !this.rotationCenter
		) {
			return false;
		}

		const activeSketchGroup = this.resolveActiveSketchGroup();
		const pointerWorld = this.resolvePointerAngleDragWorldPoint(clientX, clientY);
		if (!activeSketchGroup || !pointerWorld) {
			return false;
		}

		const referencePlane = activeSketchGroup.referencePlane;
		const started = this.pointerAngleDragSession.start(
			this.angleDeg ?? 0,
			this.rotationCenter.position,
			referencePlane.xAxis,
			referencePlane.yAxis,
			pointerWorld,
		);
		if (!started) {
			return false;
		}

		this.setPointerAngleGrabbingCursor();
		return true;
	}

	/**
	 * 根据按住状态下的鼠标位置更新旋转角度预览。
	 *
	 * @param clientX 鼠标客户端 X 坐标。
	 * @param clientY 鼠标客户端 Y 坐标。
	 * @returns 当前存在拖动会话时返回 true。
	 */
	public handlePointerAngleDragMove(clientX: number, clientY: number): boolean {
		if (!this.pointerAngleDragSession.isActive()) {
			return false;
		}

		const pointerWorld = this.resolvePointerAngleDragWorldPoint(clientX, clientY);
		if (!pointerWorld) {
			return true;
		}

		const nextAngleDeg = this.pointerAngleDragSession.update(pointerWorld);
		if (nextAngleDeg === null) {
			return true;
		}

		// 鼠标拖动、实体预览、三轴方向和 PropertyManager 共用唯一角度状态。
		this.angleDeg = nextAngleDeg;
		this.refreshPhase();
		this.tryPreview();
		this.notifyStateChanged();
		return true;
	}

	/**
	 * 结束旋转控标拖动并保留最后一帧命令预览。
	 *
	 * @returns 此前存在有效拖动会话时返回 true。
	 */
	public handlePointerAngleDragEnd(): boolean {
		if (!this.pointerAngleDragSession.isActive()) {
			return false;
		}
		this.pointerAngleDragSession.end();
		this.clearPointerAngleDragCursor();
		return true;
	}

	/**
	 * 处理视口离开或窗口失焦导致的鼠标会话取消。
	 *
	 * 取消只终止本次控标拖动，最后一帧仍是 PropertyManager 的临时预览；最终回滚继续由 Cancel/Esc 负责。
	 *
	 * @returns 此前存在有效拖动会话时返回 true。
	 */
	public handlePointerAngleDragCancel(): boolean {
		return this.handlePointerAngleDragEnd();
	}
	//------ 旋转中心三轴辅助对象 结束------

	//------ 旋转实体保留几何关系 开始------
	/**
	 * 设置旋转实体几何关系保留模式。
	 *
	 * @param mode 几何关系保留模式。
	 *
	 * 设计说明：
	 * 1. 旋转实体默认不保留关系时会临时断开水平、竖直和固定等阻塞关系；
	 * 2. 切换模式必须先回滚旧预览和旧关系上下文，避免视口中保留过期约束状态；
	 * 3. 缓存仍由父类字段承接，保证 Move / Copy / Rotate 的控制器会话行为一致。
	 */
	public override setRelationRetentionMode(mode: SketchEntitiesTransformRelationRetentionMode): void {
		this.cacheRelationRetentionMode(mode);
		if (this.relationRetentionMode === mode) {
			return;
		}
		this.rollbackPreview();
		this.relationRetentionMode = mode;
		this.tryPreview();
		this.notifyStateChanged();
	}
	//------ 旋转实体保留几何关系 结束------

	
	protected resolveCommandType(): SketchEntitiesTransformCommand {
		return SketchEntitiesTransformCommand.Rotate;
	}

	
	protected resolveTransformMatrix(): THREE.Matrix4 {
		const activeSketchGroup = this.resolveActiveSketchGroup();
		if (!activeSketchGroup || !this.rotationCenter || this.angleDeg === null) {
			return new THREE.Matrix4();
		}
		return this.geometryService.createRotationMatrix(
			this.rotationCenter.position,
			activeSketchGroup.referencePlane.normal.clone(),
			THREE.MathUtils.degToRad(this.angleDeg),
		);
	}

	
	protected applyResolvedTransform(matrix: THREE.Matrix4): void {
		if (!this.applyParametricTransform(
			this.selectedEntities,
			matrix,
			'rotate-entities',
			this.rotatePreviewBaseline ?? undefined,
		)) {
			throw new Error('旋转实体失败：统一参数系统拒绝目标位置。');
		}
	}

	//------ 旋转实体确认式历史会话 开始------
	
	protected override refreshPhase(): void {
		if (!this.active) {
			this.phase = SketchEntitiesTransformPhase.Cancelled;
			return;
		}
		if (this.canConfirm()) {
			this.phase = this.rotatePreviewApplied
				? SketchEntitiesTransformPhase.Previewing
				: SketchEntitiesTransformPhase.Ready;
			return;
		}
		this.phase = this.activeSelectionRole === SketchEntitiesTransformSelectionRole.Entities
			? SketchEntitiesTransformPhase.SelectEntities
			: SketchEntitiesTransformPhase.SelectReference;
	}

	
	protected override tryPreview(): void {
		if (!this.active) {
			return;
		}
		const activeSketchGroup = this.resolveActiveSketchGroup();
		if (!activeSketchGroup) {
			this.rollbackPreview();
			this.clearRotateCenterAxisHelper();
			return;
		}

		const matrix = this.resolveTransformMatrix();
		const axisHelperUpdated = this.syncRotateCenterAxisHelper(matrix);
		if (!this.canConfirm()) {
			const previewRollbackWillRender = this.rotatePreviewApplied
				|| this.relationRetentionPreviewContext !== null;
			this.rollbackPreview();
			// 参数暂时不完整时实体预览不会触发渲染，但有效的角度输入仍需立即刷新三轴方向。
			if (axisHelperUpdated && !previewRollbackWillRender) {
				this.viewer.requestRender();
			}
			return;
		}
		if (this.isIdentityMatrix(matrix)) {
			this.restoreRotatePreviewBaseline();
			this.rollbackRelationRetentionPreviewContext();
			this.rotatePreviewBaseline = null;
			this.rotatePreviewApplied = false;
			this.viewer.requestRender();
			this.refreshPhase();
			return;
		}

		this.ensureRotatePreviewBaseline();
		this.ensureRelationRetentionPreviewContext(activeSketchGroup);
		this.applyResolvedTransform(matrix);
		this.rotatePreviewApplied = true;
		this.viewer.requestRender();
		this.refreshPhase();
	}

	
	protected override rollbackPreview(): void {
		let shouldRequestRender = false;
		if (this.rotatePreviewApplied) {
			this.restoreRotatePreviewBaseline();
			shouldRequestRender = true;
		}

		if (this.relationRetentionPreviewContext) {
			this.rollbackRelationRetentionPreviewContext();
			shouldRequestRender = true;
		}
		this.rotatePreviewBaseline = null;
		this.rotatePreviewApplied = false;
		if (shouldRequestRender) {
			this.viewer.requestRender();
		}
		this.refreshPhase();
	}

	/**
	 * 确认当前旋转结果。
	 * @returns 当前状态与活动草图允许提交时返回 true，否则返回 false。
	 * @throws 当关系保留、几何变换、草图提交或历史提交失败时，回滚确认上下文并原样重新抛出原异常。
	 */
	public override confirm(): boolean {
		if (!this.canConfirm()) {
			return false;
		}

		const activeSketchGroup = this.resolveActiveSketchGroup();
		if (!activeSketchGroup) {
			return false;
		}
		this.resetPointerAngleDrag();

		const matrix = this.resolveTransformMatrix();
		const shouldApplyRelationRetentionForConfirm = !this.isIdentityMatrix(matrix);
		if (this.rotatePreviewApplied) {
			this.restoreRotatePreviewBaseline();
			this.rotatePreviewApplied = false;
		}
		if (this.relationRetentionPreviewContext) {
			this.rollbackRelationRetentionPreviewContext();
		}

		this.rotateRuntimeHistorySession = this.viewer.beginActiveSketchRuntimeHistorySession({
			label: this.resolveHistoryLabel(),
			commandType: this.resolveHistoryCommandType(),
		});
		let confirmRelationRetentionContext: SketchRelationRetentionContext | null = null;
		try {
			if (shouldApplyRelationRetentionForConfirm) {
				// 零角度确认不能删除几何关系，否则空提交会破坏矩形的水平/竖直设计意图。
				confirmRelationRetentionContext = this.applyRelationRetentionForConfirm(activeSketchGroup);
			}
			this.applyResolvedTransform(matrix);
			this.commitSketchGroup(activeSketchGroup);
			this.lastRepeatRecord = this.createRepeatRecord(matrix);
			this.rotatePreviewBaseline = null;
			this.phase = SketchEntitiesTransformPhase.Committed;
			confirmRelationRetentionContext?.commit();
			this.commitRotateRuntimeHistorySession();
		} catch (error) {
			confirmRelationRetentionContext?.rollback();
			this.cancelRotateRuntimeHistorySession();
			throw error;
		}
		this.notifyStateChanged();
		this.viewer.clearSketchRotateEntitiesMode();
		return true;
	}

	
	public override cancel(): void {
		this.resetPointerAngleDrag();
		this.rollbackPreview();
		this.clearRotateCenterAxisHelper();
		clearSketchEntitiesTransformPointSnapFeedback(this.viewer);
		this.phase = SketchEntitiesTransformPhase.Cancelled;
		this.notifyStateChanged();
	}
	//------ 旋转实体确认式历史会话 结束------

	//------ 旋转实体保留几何关系 开始------
	/**
	 * 确保预览阶段已经按当前选项临时处理旋转阻塞关系。
	 *
	 * @param sketchGroup 当前活动草图组。
	 *
	 * 设计说明：
	 * 1. 默认不保留几何关系时，预览必须先摆脱水平、竖直和固定关系；
	 * 2. 预览关系移除只能临时存在，Cancel 或参数变化时必须可回滚；
	 * 3. 已存在上下文时直接复用，避免角度输入连续变化时重复删除同一批约束。
	 */
	private ensureRelationRetentionPreviewContext(sketchGroup: SketchGroup): void {
		if (!this.shouldBreakRotateBlockingRelations() || this.relationRetentionPreviewContext) {
			return;
		}

		this.relationRetentionPreviewContext = this.relationRetentionService.detachRotateBlockingRelations({
			sketchGroup,
			selectedEntities: this.selectedEntities,
		});
	}

	/**
	 * 回滚预览阶段临时断开的旋转阻塞关系。
	 */
	private rollbackRelationRetentionPreviewContext(): void {
		this.relationRetentionPreviewContext?.rollback();
		this.relationRetentionPreviewContext = null;
	}

	/**
	 * 在确认阶段正式应用旋转实体的几何关系保留策略。
	 *
	 * @param sketchGroup 当前活动草图组。
	 * @returns 可在确认失败时回滚的关系上下文。
	 */
	private applyRelationRetentionForConfirm(sketchGroup: SketchGroup): SketchRelationRetentionContext | null {
		if (!this.shouldBreakRotateBlockingRelations()) {
			return null;
		}

		return this.relationRetentionService.detachRotateBlockingRelations({
			sketchGroup,
			selectedEntities: this.selectedEntities,
		});
	}

	/**
	 * 判断当前是否需要断开旋转阻塞关系。
	 *
	 * @returns 需要断开旋转阻塞关系时返回 true。
	 */
	private shouldBreakRotateBlockingRelations(): boolean {
		return this.relationRetentionMode === SketchEntitiesTransformRelationRetentionMode.BreakExternalRelations;
	}
	//------ 旋转实体保留几何关系 结束------

	
	protected resolveHistoryCommandType(): string {
		return 'sketch.runtime.rotate-entities';
	}

	
	protected resolveHistoryLabel(): string {
		return '旋转草图实体';
	}

	
	protected canConfirm(): boolean {
		return this.selectedEntities.length > 0 && !!this.rotationCenter && this.angleDeg !== null;
	}

	
	protected canRepeat(): boolean {
		return this.lastRepeatRecord !== null;
	}

	
	protected applyRepeatRecord(record: SketchEntitiesTransformRepeatRecord, sketchGroup: SketchGroup): boolean {
		const entities = this.geometryService.resolveEntitiesByIds(sketchGroup, record.sourceEntityIds);
		if (entities.length === 0) {
			return false;
		}
		this.viewer.executeActiveSketchRuntimeHistoryTransaction({
			label: this.resolveHistoryLabel(),
			commandType: this.resolveHistoryCommandType(),
			work: () => {
				if (!this.applyParametricTransform(entities, record.matrix, 'rotate-entities-repeat')) {
					throw new Error('重复旋转失败：统一参数系统拒绝目标位置。');
				}
				sketchGroup.markDefinitionDirty({
					reason: this.resolveHistoryCommandType(),
					sourceObjectId: entities[0]?.uuid,
				});
				sketchGroup.commitDefinitionChanges({
					reason: this.resolveHistoryCommandType(),
					sourceObjectId: entities[0]?.uuid,
				});
				return true;
			},
		});
		return true;
	}

	//------ 旋转实体确认式历史会话 开始------
	/**
	 * 判断矩阵是否为单位矩阵。
	 *
	 * @param matrix 待判断矩阵。
	 * @returns 若矩阵为单位矩阵则返回 true。
	 */
	private isIdentityMatrix(matrix: THREE.Matrix4): boolean {
		const identity = new THREE.Matrix4();
		const epsilon = 1e-10;
		return matrix.elements.every((value, index) => Math.abs(value - identity.elements[index]) <= epsilon);
	}

	/**
	 * 确保旋转预览基线已建立。
	 */
	private ensureRotatePreviewBaseline(): void {
		if (this.rotatePreviewBaseline) {
			return;
		}

		this.rotatePreviewBaseline = new Map();
		this.geometryService.collectParametricRigidTransformPoints(this.selectedEntities).forEach((point) => {
			this.rotatePreviewBaseline?.set(point.uuid, point.getPosition().clone());
		});
	}

	/**
	 * 从旋转预览基线恢复草图点位。
	 */
	private restoreRotatePreviewBaseline(): void {
		if (!this.rotatePreviewBaseline) {
			return;
		}

		if (!this.restoreParametricBaseline(
			this.selectedEntities,
			this.rotatePreviewBaseline,
			'rotate-entities-rollback',
		)) {
			throw new Error('旋转实体回滚失败：统一参数系统拒绝原始点位。');
		}
	}

	/**
	 * 提交旋转实体运行时历史会话。
	 */
	private commitRotateRuntimeHistorySession(): void {
		const historySession = this.rotateRuntimeHistorySession;
		this.rotateRuntimeHistorySession = null;

		const commitResult = historySession?.commit();
		if (commitResult && typeof (commitResult as PromiseLike<void>).then === 'function') {
			void (commitResult as PromiseLike<void>).then(undefined, (error) => {
				(globalThis as { reportError?: (reportedError: unknown) => void }).reportError?.(error);
			});
		}
	}

	/**
	 * 取消旋转实体运行时历史会话。
	 */
	private cancelRotateRuntimeHistorySession(): void {
		const historySession = this.rotateRuntimeHistorySession;
		this.rotateRuntimeHistorySession = null;
		historySession?.cancel();
	}
	//------ 旋转实体确认式历史会话 结束------

	//------ 旋转中心三轴辅助对象 开始------
	/**
	 * 同步旋转中心三轴辅助对象。
	 *
	 * 设计说明：
	 * 1. 辅助对象只在旋转中心已经选择后显示；
	 * 2. 三个方向先取当前活动草图参考面的 X、Y、法向 Z，再应用与实体预览完全相同的旋转矩阵；
	 * 3. 每次都从参考面初始坐标系按当前绝对角度重算，避免连续输入角度产生累计误差；
	 * 4. 对象挂在 Viewer 场景层，不写入草图组，避免进入草图保存、Undo 或选择链路。
	 *
	 * @param rotationMatrix 当前旋转实体预览使用的世界变换矩阵。
	 * @returns 三轴辅助对象存在且已经完成方向同步时返回 true。
	 */
	private syncRotateCenterAxisHelper(rotationMatrix: THREE.Matrix4): boolean {
		if (!this.rotationCenter) {
			this.clearRotateCenterAxisHelper();
			return false;
		}

		const activeSketchGroup = this.resolveActiveSketchGroup();
		if (!activeSketchGroup) {
			this.clearRotateCenterAxisHelper();
			return false;
		}

		const helper = this.ensureRotateCenterAxisHelper();
		const referencePlane = activeSketchGroup.referencePlane;
		helper.updateFrame({
			centerWorld: this.rotationCenter.position,
			xAxisWorld: referencePlane.xAxis.transformDirection(rotationMatrix),
			yAxisWorld: referencePlane.yAxis.transformDirection(rotationMatrix),
			zAxisWorld: referencePlane.normal.clone().transformDirection(rotationMatrix),
		});
		return true;
	}

	/**
	 * 确保旋转中心三轴辅助对象已经创建并挂载。
	 *
	 * @returns 旋转中心三轴辅助对象。
	 */
	private ensureRotateCenterAxisHelper(): SketchRotateCenterAxisHelper {
		if (this.rotateCenterAxisHelper) {
			return this.rotateCenterAxisHelper;
		}

		const helper = new SketchRotateCenterAxisHelper();
		helper.bindScreenSizeContext(
			() => this.viewer.getCamera(),
			() => this.viewer.getContainer(),
		);
		this.viewer.addObject(helper);
		this.rotateCenterAxisHelper = helper;
		return helper;
	}

	/**
	 * 清理旋转中心三轴辅助对象。
	 */
	private clearRotateCenterAxisHelper(): void {
		this.resetPointerAngleDrag();
		const helper = this.rotateCenterAxisHelper;
		if (!helper) {
			return;
		}

		helper.parent?.remove(helper);
		helper.dispose();
		this.rotateCenterAxisHelper = null;
		this.viewer.requestRender();
	}

	/**
	 * 解析鼠标与活动草图平面的世界交点。
	 *
	 * @param clientX 鼠标客户端 X 坐标。
	 * @param clientY 鼠标客户端 Y 坐标。
	 * @returns 有效交点；视口或活动草图不可用时返回 null。
	 */
	private resolvePointerAngleDragWorldPoint(clientX: number, clientY: number): THREE.Vector3 | null {
		const activeSketchGroup = this.resolveActiveSketchGroup();
		if (!activeSketchGroup) {
			return null;
		}
		return this.planePointerIntersectionResolver.resolve(
			this.viewer,
			activeSketchGroup.referencePlane,
			clientX,
			clientY,
		);
	}

	/**
	 * 重置鼠标角度拖动与专用光标。
	 */
	private resetPointerAngleDrag(): void {
		this.pointerAngleDragSession.end();
		this.clearPointerAngleDragCursor();
	}

	/**
	 * 显示旋转控标拖动中光标。
	 */
	private setPointerAngleGrabbingCursor(): void {
		if (this.hasPointerAngleGrabbingCursor) {
			return;
		}
		this.viewer.cursorManager.push(ViewportCursorStyle.Grabbing);
		this.hasPointerAngleGrabbingCursor = true;
	}

	/**
	 * 清理旋转控标压入的所有光标状态。
	 */
	private clearPointerAngleDragCursor(): void {
		if (this.hasPointerAngleGrabbingCursor) {
			this.viewer.cursorManager.pop(ViewportCursorStyle.Grabbing);
			this.hasPointerAngleGrabbingCursor = false;
		}
	}
	//------ 旋转中心三轴辅助对象 结束------
}
